import { MAX_TAKE_AUDIO_BYTES } from './assets';

export type AudioTakeRecording = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  /** True when the cap stopped the encoder before the musician finished playing. */
  truncated: boolean;
};

/**
 * Safari encodes MP4/AAC and rejects the WebM types; Chrome and Firefox prefer
 * Opus. The first supported entry wins so every platform keeps a playable take.
 */
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export function pickTakeAudioMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const supported = PREFERRED_MIME_TYPES.find((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  });
  // An empty string is a valid MediaRecorder argument meaning "browser default".
  return supported ?? '';
}

export const isTakeAudioRecordingSupported = () =>
  typeof MediaRecorder !== 'undefined' && pickTakeAudioMimeType() !== null;

/**
 * Retains the original acoustic performance alongside the recognized notes.
 *
 * It records the microphone stream that recognition already opened, at the device's
 * own sample rate: the detector's 16 kHz resampling is an analysis detail, and a
 * musician reviewing a take needs to hear the piano, not the detector's input.
 */
export class AudioTakeRecorder {
  private recorder: MediaRecorder | null = null;
  private readonly chunks: Blob[] = [];
  private byteLength = 0;
  private truncated = false;
  private startedAtMs = 0;
  private stoppedAtMs = 0;
  private readonly mimeType: string;

  constructor(
    private readonly stream: MediaStream,
    private readonly maxBytes: number = MAX_TAKE_AUDIO_BYTES,
  ) {
    const mimeType = pickTakeAudioMimeType();
    if (mimeType === null)
      throw new Error('This browser cannot keep the original recording of a take.');
    this.mimeType = mimeType;
  }

  get active() {
    return this.recorder?.state === 'recording' || this.recorder?.state === 'paused';
  }

  start(timesliceMs = 1000) {
    if (this.recorder) throw new Error('This take recorder has already started.');
    this.recorder = this.mimeType
      ? new MediaRecorder(this.stream, { mimeType: this.mimeType })
      : new MediaRecorder(this.stream);
    this.recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;
      if (this.byteLength + event.data.size > this.maxBytes) {
        this.truncated = true;
        this.requestStop();
        return;
      }
      this.byteLength += event.data.size;
      this.chunks.push(event.data);
    };
    this.startedAtMs = performance.now();
    this.recorder.start(timesliceMs);
  }

  private requestStop() {
    if (this.recorder?.state === 'recording') {
      this.stoppedAtMs = performance.now();
      this.recorder.stop();
    }
  }

  /**
   * Resolves with the finished recording, or null when nothing was captured. Never
   * rejects: a failed encoder must not lose the notes that were recognized.
   */
  stop(): Promise<AudioTakeRecording | null> {
    const recorder = this.recorder;
    if (!recorder) return Promise.resolve(null);
    if (recorder.state === 'inactive') return Promise.resolve(this.buildResult());
    return new Promise((resolve) => {
      const settle = () => {
        recorder.onstop = null;
        recorder.onerror = null;
        resolve(this.buildResult());
      };
      recorder.onstop = settle;
      recorder.onerror = settle;
      this.requestStop();
    });
  }

  /** Discards the recording without producing a blob, for an abandoned take. */
  cancel() {
    const recorder = this.recorder;
    this.recorder = null;
    this.chunks.length = 0;
    this.byteLength = 0;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch {
        // A recorder that already stopped needs no further handling.
      }
    }
  }

  private buildResult(): AudioTakeRecording | null {
    if (this.chunks.length === 0) return null;
    const endedAtMs = this.stoppedAtMs || performance.now();
    const blob = new Blob(this.chunks, { type: this.mimeType || this.chunks[0].type });
    return {
      blob,
      mimeType: blob.type || this.mimeType || 'audio/webm',
      durationMs: Math.max(0, endedAtMs - this.startedAtMs),
      truncated: this.truncated,
    };
  }
}
